// Menú desplegable anclado a un botón (estilo de la ficha 01: #242424, radio 13, filas de 30 px).
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

export interface MenuEntry {
  label?: string;
  shortcut?: string;
  onSelect?: () => void;
  disabled?: boolean;
  danger?: boolean;
  separator?: boolean;
  title?: string;
}

interface Props {
  anchor: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  items: MenuEntry[];
  align?: 'start' | 'end';
  width?: number;
}

export function Menu({ anchor, open, onClose, items, align = 'start', width }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchor.current || !ref.current) return;
    const a = anchor.current.getBoundingClientRect();
    const m = ref.current.getBoundingClientRect();
    let left = align === 'end' ? a.right - m.width : a.left;
    let top = a.bottom + 4;
    left = Math.max(8, Math.min(left, window.innerWidth - m.width - 8));
    if (top + m.height > window.innerHeight - 8) top = Math.max(8, a.top - m.height - 4);
    setPos({ left, top });
  }, [open, anchor, align, items.length]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!ref.current?.contains(target) && !anchor.current?.contains(target)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const list = [...(ref.current?.querySelectorAll<HTMLButtonElement>('.menu-item:not(:disabled)') ?? [])];
        const i = list.indexOf(document.activeElement as HTMLButtonElement);
        const next = e.key === 'ArrowDown' ? (i + 1) % list.length : (i - 1 + list.length) % list.length;
        list[next]?.focus();
        e.preventDefault();
      }
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, anchor]);

  if (!open) return null;
  return createPortal(
    <div
      ref={ref}
      className="menu"
      role="menu"
      style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999, ...(width ? { width } : {}) }}
    >
      {items.map((it, i) =>
        it.separator ? (
          <div key={i} className="menu-sep" role="separator" />
        ) : (
          <button
            key={i}
            type="button"
            role="menuitem"
            className={`menu-item${it.danger ? ' danger' : ''}`}
            disabled={it.disabled}
            title={it.title}
            onClick={() => {
              onClose();
              it.onSelect?.();
            }}
          >
            <span className="ellipsis">{it.label}</span>
            {it.shortcut ? <span className="shortcut">{it.shortcut}</span> : null}
          </button>
        ),
      )}
    </div>,
    document.body,
  );
}

// Atajo: un botón que abre un menú.
export function useMenuState() {
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return { anchor, open, toggle: () => setOpen((o) => !o), close: () => setOpen(false) };
}

export function MenuButton(props: { className?: string; title?: string; children: ReactNode; items: MenuEntry[]; align?: 'start' | 'end'; width?: number }) {
  const m = useMenuState();
  return (
    <>
      <button
        ref={m.anchor}
        type="button"
        className={props.className ?? 'btn icon'}
        title={props.title}
        aria-haspopup="menu"
        aria-expanded={m.open}
        onClick={(e) => {
          e.stopPropagation();
          m.toggle();
        }}
      >
        {props.children}
      </button>
      <Menu anchor={m.anchor} open={m.open} onClose={m.close} items={props.items} align={props.align ?? 'start'} {...(props.width ? { width: props.width } : {})} />
    </>
  );
}
